import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './FormBuilder.css';

const FormBuilder = () => {
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [fields, setFields] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'ssn', label: 'Social Security Number' },
    { value: 'textarea', label: 'Textarea' },
    { value: 'select', label: 'Dropdown' },
    { value: 'radio', label: 'Radio Buttons' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'date', label: 'Date' }
  ];

  const addField = () => {
    setFields([...fields, {
      name: '',
      label: '',
      type: 'text',
      required: false,
      options: [],
      placeholder: '',
      allows_multiple: false // For multi-child forms
    }]);
  };

  const updateField = (index, updates) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], ...updates };
    setFields(newFields);
  };

  const removeField = (index) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  const moveField = (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === fields.length - 1)) {
      return;
    }
    const newFields = [...fields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];
    setFields(newFields);
  };

  const addOption = (fieldIndex) => {
    const newFields = [...fields];
    if (!newFields[fieldIndex].options) {
      newFields[fieldIndex].options = [];
    }
    newFields[fieldIndex].options.push('');
    setFields(newFields);
  };

  const updateOption = (fieldIndex, optionIndex, value) => {
    const newFields = [...fields];
    newFields[fieldIndex].options[optionIndex] = value;
    setFields(newFields);
  };

  const removeOption = (fieldIndex, optionIndex) => {
    const newFields = [...fields];
    newFields[fieldIndex].options = newFields[fieldIndex].options.filter((_, i) => i !== optionIndex);
    setFields(newFields);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formName.trim()) {
      setError('Form name is required');
      return;
    }

    if (fields.length === 0) {
      setError('At least one field is required');
      return;
    }

    // Validate fields
    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      if (!field.name.trim() || !field.label.trim()) {
        setError(`Field ${i + 1}: Name and Label are required`);
        return;
      }
      if ((field.type === 'select' || field.type === 'radio') && (!field.options || field.options.length === 0)) {
        setError(`Field ${i + 1}: Options are required for ${field.type} fields`);
        return;
      }
    }

    // Check for duplicate field names
    const fieldNames = fields.map(f => f.name.toLowerCase());
    const duplicates = fieldNames.filter((name, index) => fieldNames.indexOf(name) !== index);
    if (duplicates.length > 0) {
      setError('Duplicate field names are not allowed');
      return;
    }

    setLoading(true);

    try {
      const formData = {
        form_name: formName,
        form_description: formDescription,
        form_data: {
          fields: fields.map(f => ({
            name: f.name.trim(),
            label: f.label.trim(),
            type: f.type,
            required: f.required,
            placeholder: f.placeholder || '',
            allows_multiple: f.allows_multiple || false,
            options: f.type === 'select' || f.type === 'radio' ? f.options.filter(o => o.trim()) : undefined
          }))
        }
      };

      await axios.post('/api/forms', formData);
      navigate('/superadmin/forms');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to create form');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-builder">
      <h2>Form Builder</h2>
      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-builder-header">
          <div className="form-group">
            <label>Form Name *</label>
            <input
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              required
              placeholder="e.g., Student Information Form"
            />
          </div>

          <div className="form-group">
            <label>Form Description</label>
            <textarea
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              rows="3"
              placeholder="Describe what this form is for..."
            />
          </div>
        </div>

        <div className="form-builder-fields">
          <div className="fields-header">
            <h3>Form Fields</h3>
            <button type="button" onClick={addField} className="btn-secondary">+ Add Field</button>
          </div>

          {fields.length === 0 ? (
            <div className="no-fields">
              <p>No fields added yet. Click "Add Field" to start building your form.</p>
            </div>
          ) : (
            <div className="fields-list">
              {fields.map((field, index) => (
                <div key={index} className="field-editor">
                  <div className="field-header">
                    <span className="field-number">{index + 1}</span>
                    <div className="field-actions">
                      <button
                        type="button"
                        onClick={() => moveField(index, 'up')}
                        disabled={index === 0}
                        className="btn-icon"
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moveField(index, 'down')}
                        disabled={index === fields.length - 1}
                        className="btn-icon"
                        title="Move down"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() => removeField(index)}
                        className="btn-icon btn-danger"
                        title="Remove field"
                      >
                        ×
                      </button>
                    </div>
                  </div>

                  <div className="field-body">
                    <div className="field-row">
                      <div className="form-group">
                        <label>Field Name * (internal identifier)</label>
                        <input
                          type="text"
                          value={field.name}
                          onChange={(e) => updateField(index, { name: e.target.value })}
                          placeholder="e.g., first_name"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Field Label * (displayed to users)</label>
                        <input
                          type="text"
                          value={field.label}
                          onChange={(e) => updateField(index, { label: e.target.value })}
                          placeholder="e.g., First Name"
                          required
                        />
                      </div>
                    </div>

                    <div className="field-row">
                      <div className="form-group">
                        <label>Field Type *</label>
                        <select
                          value={field.type}
                          onChange={(e) => updateField(index, { type: e.target.value, options: (e.target.value === 'select' || e.target.value === 'radio') ? (field.options || []) : undefined })}
                          required
                        >
                          {fieldTypes.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>
                          <input
                            type="checkbox"
                            checked={field.required || false}
                            onChange={(e) => updateField(index, { required: e.target.checked })}
                          />
                          {' '}Required
                        </label>
                      </div>

                      <div className="form-group">
                        <label>
                          <input
                            type="checkbox"
                            checked={field.allows_multiple || false}
                            onChange={(e) => updateField(index, { allows_multiple: e.target.checked })}
                          />
                          {' '}Allows Multiple Entries (for multi-child forms)
                        </label>
                      </div>
                    </div>

                    {(field.type === 'text' || field.type === 'email' || field.type === 'phone' || field.type === 'number') && (
                      <div className="form-group">
                        <label>Placeholder Text</label>
                        <input
                          type="text"
                          value={field.placeholder || ''}
                          onChange={(e) => updateField(index, { placeholder: e.target.value })}
                          placeholder="e.g., Enter your first name"
                        />
                      </div>
                    )}

                    {(field.type === 'select' || field.type === 'radio') && (
                      <div className="form-group">
                        <label>Options *</label>
                        {field.options && field.options.map((option, optIndex) => (
                          <div key={optIndex} className="option-row">
                            <input
                              type="text"
                              value={option}
                              onChange={(e) => updateOption(index, optIndex, e.target.value)}
                              placeholder={`Option ${optIndex + 1}`}
                              required
                            />
                            <button
                              type="button"
                              onClick={() => removeOption(index, optIndex)}
                              className="btn-icon btn-danger"
                              title="Remove option"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => addOption(index)}
                          className="btn-secondary btn-small"
                        >
                          + Add Option
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Creating...' : 'Create Form'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/superadmin/forms')}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormBuilder;

